
import { useState, FormEvent } from 'react';

const advantages = [
  { icon: 'ri-settings-3-line', label: 'Full-Cycle EPC', desc: 'Design → Build → Commission' },
  { icon: 'ri-flask-line', label: 'Own R&D Lab', desc: 'In-house technology development' },
  { icon: 'ri-building-2-line', label: '2,500 m² Facility', desc: 'Own production capacity' },
  { icon: 'ri-timer-line', label: '24h Response', desc: 'Fast technical assessment' },
];

export default function CTASection() {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');
    const form = e.currentTarget;
    const formData = new FormData(form);
    try {
      const response = await fetch('https://readdy.ai/api/form/d6gqhsri06gv2c9ufjp0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as any).toString(),
      });
      if (response.ok) {
        setSubmitStatus('success');
        form.reset();
      } else {
        setSubmitStatus('error');
      }
    } catch {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section className="bg-[#0A1628] relative overflow-hidden">
      {/* Subtle grid */}
      <div className="absolute inset-0 opacity-[0.04]" style={{ backgroundImage: 'linear-gradient(#00D4FF 1px, transparent 1px), linear-gradient(90deg, #00D4FF 1px, transparent 1px)', backgroundSize: '60px 60px' }}></div>
      {/* Glow */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[400px] bg-[#0066FF]/10 rounded-full blur-[120px] pointer-events-none"></div>

      <div className="relative max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 pt-16 lg:pt-24 pb-0">

        {/* Top: headline + advantages */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-20 items-start mb-14 lg:mb-20">

          {/* Left: headline */}
          <div>
            <div className="w-10 h-[3px] bg-[#00D4FF] mb-6"></div>
            <h2 className="text-[32px] sm:text-[44px] lg:text-[64px] font-black text-white uppercase leading-[1.0] tracking-tight mb-6">
              LET'S BUILD<br />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#0066FF] to-[#00D4FF]">YOUR SOLUTION</span><br />
              TOGETHER
            </h2>
            <p className="text-[#8A9BB5] text-base leading-relaxed max-w-[440px]">
              Tell us about your project — our engineers will prepare a tailored technical proposal within 24 hours.
            </p>
          </div>

          {/* Right: advantages */}
          <div className="grid grid-cols-2 gap-4 lg:gap-5 pt-2">
            {advantages.map((adv, i) => (
              <div key={i} className="flex items-start gap-4 p-5 rounded-[16px] border border-white/[0.07] bg-white/[0.03] hover:bg-white/[0.06] transition-colors duration-300">
                <div className="w-9 h-9 flex items-center justify-center rounded-[10px] bg-[#0066FF]/20 flex-shrink-0">
                  <i className={`${adv.icon} text-[#00D4FF] text-lg`}></i>
                </div>
                <div>
                  <div className="text-white text-sm font-bold leading-tight mb-1">{adv.label}</div>
                  <div className="text-[#8A9BB5] text-xs leading-snug">{adv.desc}</div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Divider */}
        <div className="w-full h-px bg-white/[0.08] mb-14 lg:mb-20"></div>

        {/* Bottom: form full-width */}
        <div className="pb-16 lg:pb-24">
          {submitStatus === 'success' ? (
            <div className="flex flex-col items-center justify-center py-16 gap-6">
              <div className="w-16 h-16 flex items-center justify-center rounded-full bg-[#0066FF]/20">
                <i className="ri-checkbox-circle-line text-[#00D4FF] text-4xl"></i>
              </div>
              <div className="text-center">
                <div className="text-white text-2xl font-black mb-2">Request Submitted!</div>
                <div className="text-[#8A9BB5] text-base">Our engineering team will contact you within 24 hours.</div>
              </div>
            </div>
          ) : (
            <form id="cta-consultation-form" onSubmit={handleSubmit} data-readdy-form>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
                <div className="flex flex-col gap-1.5">
                  <label className="text-[10px] uppercase tracking-[2px] text-[#8A9BB5] font-bold">Full Name *</label>
                  <input
                    type="text"
                    name="name"
                    required
                    placeholder="John Smith"
                    className="bg-white/[0.05] border border-white/[0.1] rounded-[12px] px-4 py-3 text-white text-sm placeholder-[#4A5568] focus:outline-none focus:border-[#0066FF] transition-colors"
                  />
                </div>
                <div className="flex flex-col gap-1.5">
                  <label className="text-[10px] uppercase tracking-[2px] text-[#8A9BB5] font-bold">Email *</label>
                  <input
                    type="email"
                    name="email"
                    required
                    placeholder="john@company.com"
                    className="bg-white/[0.05] border border-white/[0.1] rounded-[12px] px-4 py-3 text-white text-sm placeholder-[#4A5568] focus:outline-none focus:border-[#0066FF] transition-colors"
                  />
                </div>
                <div className="flex flex-col gap-1.5">
                  <label className="text-[10px] uppercase tracking-[2px] text-[#8A9BB5] font-bold">Company *</label>
                  <input
                    type="text"
                    name="company"
                    required
                    placeholder="Company Ltd."
                    className="bg-white/[0.05] border border-white/[0.1] rounded-[12px] px-4 py-3 text-white text-sm placeholder-[#4A5568] focus:outline-none focus:border-[#0066FF] transition-colors"
                  />
                </div>
                <div className="flex flex-col gap-1.5">
                  <label className="text-[10px] uppercase tracking-[2px] text-[#8A9BB5] font-bold">Industry *</label>
                  <select
                    name="industry"
                    required
                    className="bg-white/[0.05] border border-white/[0.1] rounded-[12px] px-4 py-3 text-white text-sm focus:outline-none focus:border-[#0066FF] transition-colors cursor-pointer appearance-none"
                  >
                    <option value="" className="bg-[#0A1628]">Select sector</option>
                    <option value="oil-gas" className="bg-[#0A1628]">Oil &amp; Gas</option>
                    <option value="chemical" className="bg-[#0A1628]">Chemical Industry</option>
                    <option value="metallurgy" className="bg-[#0A1628]">Metallurgy</option>
                    <option value="energy" className="bg-[#0A1628]">Energy Sector</option>
                    <option value="industrial" className="bg-[#0A1628]">Industrial</option>
                    <option value="other" className="bg-[#0A1628]">Other</option>
                  </select>
                </div>
              </div>

              <div className="flex flex-col md:flex-row gap-4 items-end">
                <div className="flex flex-col gap-1.5 flex-1">
                  <label className="text-[10px] uppercase tracking-[2px] text-[#8A9BB5] font-bold">Project Details</label>
                  <textarea
                    name="message"
                    rows={2}
                    maxLength={500}
                    placeholder="Describe your water treatment requirements, capacity, or specific challenges..."
                    className="bg-white/[0.05] border border-white/[0.1] rounded-[12px] px-4 py-3 text-white text-sm placeholder-[#4A5568] focus:outline-none focus:border-[#0066FF] transition-colors resize-none"
                  ></textarea>
                </div>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="group flex items-center gap-3 bg-gradient-to-r from-[#0066FF] to-[#0052CC] hover:from-[#0052CC] hover:to-[#003D99] disabled:opacity-60 text-white px-8 py-[14px] rounded-[12px] text-sm font-black uppercase tracking-wide transition-all duration-300 whitespace-nowrap cursor-pointer flex-shrink-0 self-end"
                >
                  {isSubmitting ? (
                    <><i className="ri-loader-4-line text-lg animate-spin"></i> Sending...</>
                  ) : (
                    <>Request Consultation <i className="ri-arrow-right-line text-lg group-hover:translate-x-1 transition-transform"></i></>
                  )}
                </button>
              </div>

              {submitStatus === 'error' && (
                <div className="mt-4 text-sm text-red-400 flex items-center gap-2">
                  <i className="ri-error-warning-line"></i> Something went wrong. Please try again.
                </div>
              )}
            </form>
          )}
        </div>

      </div>
    </section>
  );
}
